import { FormattedMessage } from 'react-intl';
import { Tile } from 'components/Layout/PageContainer';
import * as List from 'components/Layout/Tiles/ListContainer';
import { Interpretation } from 'components/MeasureComparison';
import { Measure, MeasureValues } from 'services/model/model.service';
import { cn } from 'utils/classNamesTailwind';

export interface MeasureBreakdownRow {
  labelMeasure?: Measure;
  perItemMeasure?: Measure;
  totalMeasure?: Measure;
  shareMeasure?: Measure;
  isTotal?: boolean;
}

interface MeasureBreakdownListProps {
  titleId?: string;
  titleMeasure?: Measure;
  rows: MeasureBreakdownRow[];
  values?: MeasureValues;
  compareValues?: MeasureValues;
  isComparisonActive: boolean;
  testId?: string;
}

function RowLabel({ labelMeasure = undefined }: { labelMeasure?: Measure }) {
  if (labelMeasure?.labelId) {
    return <FormattedMessage id={labelMeasure.labelId} />;
  }
  return <>{labelMeasure?.metric.name ?? ''}</>;
}

const CURRENCY_VALUE_WIDTH = 'w-[67px]';
const PERCENT_VALUE_WIDTH = 'w-[54px]';

function MeasureValueCell({
  measure = undefined,
  values = undefined,
  compareValues = undefined,
  isComparisonActive,
  isTotal = false,
  valueClassName,
}: {
  measure?: Measure;
  values?: MeasureValues;
  compareValues?: MeasureValues;
  isComparisonActive: boolean;
  isTotal?: boolean;
  valueClassName: string;
}) {
  if (!measure) {
    return <td className="pl-6" />;
  }
  return (
    <List.MetricCell
      value={measure.getValue(values)}
      comparisonValue={measure.getValue(compareValues)}
      unit={measure.metric.unit}
      comparisonActive={isComparisonActive}
      interpretation={Interpretation.NEGATIVE}
      hideMeasureLabel
      boldValue={isTotal}
      valueClassName={valueClassName}
    />
  );
}

export function MeasureBreakdownList({
  titleId = undefined,
  titleMeasure = undefined,
  rows,
  values = undefined,
  compareValues = undefined,
  isComparisonActive,
  testId = undefined,
}: MeasureBreakdownListProps) {
  return (
    <Tile title={titleMeasure?.metric.name} titleId={titleId} testId={testId}>
      <List.ListContainer
        columnWidths={['w-40', 'w-40', 'w-52']}
        header={
          <List.ListHeaderRow className="text-[12px]">
            <List.ListHeaderCell aria-hidden />
            <List.ListHeaderCell>
              <FormattedMessage id="profitability.measureBreakdown.column.perItem" />
            </List.ListHeaderCell>
            <List.ListHeaderCell>
              <FormattedMessage id="profitability.measureBreakdown.column.total" />
            </List.ListHeaderCell>
            <List.ListHeaderCell>
              <FormattedMessage id="profitability.measureBreakdown.column.shareOfNetSoldGmv" />
            </List.ListHeaderCell>
          </List.ListHeaderRow>
        }
      >
        {rows.map((row, index) => {
          const nextRowIsTotal = rows[index + 1]?.isTotal ?? false;
          return (
            <List.ListRow
              key={row.labelMeasure?.labelId ?? row.labelMeasure?.code}
              fixedHeight={false}
              className={cn(
                '[&>td]:py-2',
                row.isTotal && 'border-t border-snow-700 font-bold',
                !row.isTotal && nextRowIsTotal && 'border-b-0'
              )}
            >
              <List.NameCell
                name={
                  <span className="text-sm">
                    <RowLabel labelMeasure={row.labelMeasure} />
                  </span>
                }
                hideColorBar
              />
              <MeasureValueCell
                measure={row.perItemMeasure}
                values={values}
                compareValues={compareValues}
                isComparisonActive={false}
                isTotal={row.isTotal}
                valueClassName={CURRENCY_VALUE_WIDTH}
              />
              <MeasureValueCell
                measure={row.totalMeasure}
                values={values}
                compareValues={compareValues}
                isComparisonActive={isComparisonActive}
                isTotal={row.isTotal}
                valueClassName={CURRENCY_VALUE_WIDTH}
              />
              <MeasureValueCell
                measure={row.shareMeasure}
                values={values}
                compareValues={compareValues}
                isComparisonActive={isComparisonActive}
                isTotal={row.isTotal}
                valueClassName={PERCENT_VALUE_WIDTH}
              />
            </List.ListRow>
          );
        })}
      </List.ListContainer>
    </Tile>
  );
}
